import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { ShiftsService } from '../api/ShiftsAPI';
import { ScheduleService } from '../api/ScheduleAPI';
import { ShiftRequestsService } from '../api/ShiftRequestsAPI';
import { StaffService } from '../api/StaffAPI';
import { useAuth } from '../store';
import { LOCATION_META } from '../constants';
import { useLightMode } from '../utils';
import { Header } from '../components/Header/Header';
import { RequestDecisionModal } from '../components/RequestDecisionModal/RequestDecisionModal';
import { SelectMenu } from '../components/SelectMenu/SelectMenu';
import s from '../styles/staff.module.css';
import d from '../styles/dashboard.module.css';

// One person's own shifts: what was planned, what was actually worked, and the
// four things they can ask a manager for. The plan comes from `shiftSchedules`
// and the clock from `shifts`; this page is where the two are put side by side.

const DAY_MS = 86400000;
const iso = (dt) => new Date(dt).toISOString().slice(0, 10);

// Which calendar day an instant belongs to, on the clock the person is
// standing next to.
//
// `toISOString` would answer in UTC, and the grid, the times and "today" all
// read local. West of UTC that files an evening shift under tomorrow and rolls
// the page over to the next day mid-afternoon: a 7 PM clock-in in Vancouver
// landed on the following column and the day panel never changed.
const localDay = (v) => {
  const dt = new Date(v);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};
const utcDay = (key) => new Date(`${key}T00:00:00Z`);

// Weeks run Sunday to Saturday, matching the server's own weekStart().
const weekStartOf = (key) => {
  const dt = utcDay(key);
  dt.setUTCDate(dt.getUTCDate() - dt.getUTCDay());
  return iso(dt);
};
const addDays = (key, n) => iso(new Date(utcDay(key).getTime() + n * DAY_MS));

// "8h 48m", "39m" — the mockup never prints bare minutes over an hour.
const dur = (mins) => {
  const n = Math.max(0, Math.round(mins || 0));
  if (!n) return '0m';
  const h = Math.floor(n / 60);
  return h ? `${h}h${n % 60 ? ` ${n % 60}m` : ''}` : `${n}m`;
};
const hrsShort = (mins) => (mins ? `${+(mins / 60).toFixed(mins % 60 ? 1 : 0)}h` : '');

const tm = (v) => (v ? new Date(v).toLocaleTimeString('en-US',
  { hour: 'numeric', minute: '2-digit' }) : '—');
const dLong = (key) => utcDay(key).toLocaleDateString('en-US',
  { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC' });
const dShort = (key) => utcDay(key).toLocaleDateString('en-US',
  { month: 'short', day: 'numeric', timeZone: 'UTC' });
const dowOf = (key) => utcDay(key).toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' });

// Minutes from midnight, in the viewer's own zone — the grid is drawn against
// the clock on the wall, not against UTC.
const minsIntoDay = (v) => {
  const dt = new Date(v);
  return dt.getHours() * 60 + dt.getMinutes();
};

// The grid covers 6 AM to 10 PM, which is where this shop's work actually
// falls. Anything outside is clamped rather than dropped.
const GRID_FROM = 6 * 60;
const GRID_TO = 22 * 60;
const GRID_SPAN = GRID_TO - GRID_FROM;
const pct = (mins) => `${Math.min(100, Math.max(0, ((mins - GRID_FROM) / GRID_SPAN) * 100))}%`;
const span = (from, to) => `calc(${pct(to)} - ${pct(from)})`;

const TILES = [
  { kind: 'time_off',        label: 'Request time off',
    icon: 'M8 2v3M16 2v3M3 9h18M5 5h14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2z' },
  { kind: 'day_unavailable', label: 'Mark a day unavailable',
    icon: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM6 6l12 12' },
  { kind: 'hours_change',    label: 'Change my hours',
    icon: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM12 7v5l3 2' },
  { kind: 'clock_fix',       label: 'Fix a clock time',
    icon: 'M4 20h4L19 9a2 2 0 0 0-3-3L5 17v3z' },
];

const STATUS_TONE = {
  pending: 'attnToneWarn', approved: 'attnToneGray',
  declined: 'attnToneDue', withdrawn: 'attnToneGray',
};

const REQ_LABEL = {
  time_off: 'Time off', day_unavailable: 'Day unavailable',
  hours_change: 'Change of hours', clock_fix: 'Clock time fix',
};

const fullName = (m) => m.name
  || `${m.given_name || ''} ${m.family_name || ''}`.trim()
  || m.externalId;

// Which garages somebody works at, under their name in the picker. Every
// garage reads as "All" — the same shorthand the staff page uses, and four
// names is more than the line can carry anyway.
const ALL_LOCATIONS = Object.keys(LOCATION_META);
const worksAt = (m) => {
  const ids = m.locationIds || [];
  if (!ids.length) return 'No garage set';
  if (ids.length >= ALL_LOCATIONS.length) return 'All garages';
  return ids.map(id => LOCATION_META[id]?.name || id).join(', ');
};

export function ShiftsPage() {
  const [lightMode, toggleLightMode] = useLightMode();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  // Who is being read, and whether it is you.
  //
  // Acting on a timecard follows the timecard, not the role: an admin linked to
  // a roster entry is also a technician, and on their own week they clock in
  // and raise requests like anybody else. On somebody else's week those
  // buttons are not theirs to press.
  const isAdmin = user?.role === 'admin';
  const ownStaffId = user?.idStaff ?? null;
  const [viewing, setViewing] = useState(ownStaffId);
  const idStaff = isAdmin ? viewing : ownStaffId;
  const isSelf = !!ownStaffId && String(idStaff) === String(ownStaffId);
  const canAct = isSelf;

  const today = localDay(new Date());
  const [view, setView] = useState('week');
  const [anchor, setAnchor] = useState(today);
  const [selected, setSelected] = useState(today);
  const [sheet, setSheet] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // The window the calendar is showing, which is also what the totals cover.
  const window_ = useMemo(() => {
    if (view === 'week') {
      const from = weekStartOf(anchor);
      return { start: from, end: addDays(from, 6) };
    }
    const first = `${anchor.slice(0, 7)}-01`;
    const dt = utcDay(first);
    dt.setUTCMonth(dt.getUTCMonth() + 1);
    dt.setUTCDate(0);
    return { start: first, end: iso(dt) };
  }, [view, anchor]);

  const fetchRange = useMemo(() => ({
    start: addDays(window_.start, -1), end: addDays(window_.end, 1),
  }), [window_.start, window_.end]);

  const { data: staffData } = useQuery({
    queryKey: ['staff-roster'],
    queryFn: () => StaffService.list('active'),
    enabled: isAdmin,
    staleTime: Infinity,
  });
  const roster = staffData?.staffs || [];

  // An admin arriving with no choice made gets the first technician rather
  // than an empty page.
  useEffect(() => {
    if (isAdmin && !viewing && roster.length) setViewing(String(roster[0].idStaff));
  }, [isAdmin, viewing, roster]);

  const viewingName = useMemo(() => {
    if (!isAdmin) return null;
    const m = roster.find(r => String(r.idStaff) === String(idStaff));
    return m ? fullName(m) : null;
  }, [isAdmin, roster, idStaff]);

  const { data, isLoading } = useQuery({
    queryKey: ['my-shifts', idStaff, fetchRange.start, fetchRange.end],
    queryFn: () => ScheduleService.mine(fetchRange.start, fetchRange.end, idStaff),
    enabled: !!idStaff,
    placeholderData: (prev) => prev,
  });

  const { data: openData } = useQuery({
    queryKey: ['shift-open', idStaff],
    queryFn: () => ShiftsService.openFor(idStaff),
    enabled: !!idStaff,
    refetchInterval: 60000,
  });

  const { data: upcomingData } = useQuery({
    queryKey: ['shift-upcoming', idStaff],
    queryFn: () => ScheduleService.upcoming(4),
    enabled: !!idStaff,
  });

  const openShift = openData?.shift || null;
  const coverage = data?.coverage || {};
  const requests = data?.requests || [];
  const posted = data?.posted || {};
  const upcoming = upcomingData?.shifts || [];

  // The server groups by UTC day because that is all it can know; the page
  // regroups by the viewer's own day, which is the one on the wall behind the
  // calendar. Everything downstream — the columns, the bars, the totals —
  // reads these, so there is one answer to "which day was that".
  const inWindow = useCallback(
    (key) => key >= window_.start && key <= window_.end, [window_.start, window_.end]);

  const scheduled = useMemo(
    () => (data?.scheduled || [])
      .map(x => ({ ...x, day: localDay(x.startAt) }))
      .filter(x => inWindow(x.day)),
    [data, inWindow]);

  const worked = useMemo(
    () => (data?.worked || [])
      .map(w => ({ ...w, day: localDay(w.clockInAt) }))
      .filter(w => inWindow(w.day)),
    [data, inWindow]);

  const byDay = useMemo(() => {
    const m = {};
    const touch = (k) => (m[k] ||= { day: k, scheduledMinutes: 0, workedMinutes: 0, off: null, pending: false });
    for (const x of scheduled) touch(x.day).scheduledMinutes += x.minutes || 0;
    for (const w of worked) touch(w.day).workedMinutes += w.minutes || 0;
    // `off` and `pending` are plain dates with no time in them, so they need no
    // converting — they are already the day somebody asked about.
    for (const day of (data?.days || [])) {
      if (!day.off && !day.pending) continue;
      if (!inWindow(day.day)) continue;
      const cur = touch(day.day);
      cur.off = day.off;
      cur.pending = day.pending;
    }
    return m;
  }, [scheduled, worked, data, inWindow]);

  const days = useMemo(() => Object.values(byDay).sort((a, b) => a.day.localeCompare(b.day)), [byDay]);

  // Totals follow the regrouped days rather than the server's, so the figure in
  // the corner matches the bars under it.
  const totals = useMemo(() => ({
    scheduledMinutes: scheduled.reduce((n, x) => n + (x.minutes || 0), 0),
    workedMinutes: worked.reduce((n, w) => n + (w.minutes || 0), 0),
    scheduledShifts: scheduled.length,
    shifts: worked.length,
  }), [scheduled, worked]);

  const weekPosted = !!posted[weekStartOf(anchor)];

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['my-shifts'] });
    queryClient.invalidateQueries({ queryKey: ['shift-open'] });
    queryClient.invalidateQueries({ queryKey: ['shift-upcoming'] });
  };

  // Three clock states, as the mockup has them: off the clock, on it, and done
  // for the day — the last being what you see after clocking out.
  const todaySched = scheduled.find((x) => x.day === today) || null;
  const todayWorked = worked.filter((w) => w.day === today);
  const clockState = openShift ? 'on' : (todayWorked.length ? 'done' : 'off');
  const lastToday = todayWorked[todayWorked.length - 1] || null;

  const punch = async () => {
    if (!canAct) return;
    setBusy(true);
    setError('');
    try {
      if (openShift) await ShiftsService.clockOut({ idShift: openShift.idShift });
      else await ShiftsService.clockIn(idStaff, todaySched?.location || null, null);
      refresh();
    } catch (err) {
      setError(err?.error || err?.message || 'That did not go through. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const decide = async (request, approve, note) => {
    setBusy(true);
    setError('');
    try {
      if (approve) await ShiftRequestsService.approve(request.idRequest, note);
      else await ShiftRequestsService.decline(request.idRequest, note);
      setSheet(null);
      refresh();
    } catch (err) {
      setError(err?.error || err?.message || 'Could not record that decision.');
    } finally {
      setBusy(false);
    }
  };

  const step = (n) => setAnchor(view === 'week'
    ? addDays(anchor, n * 7)
    : iso(new Date(Date.UTC(utcDay(anchor).getUTCFullYear(), utcDay(anchor).getUTCMonth() + n, 1))));

  const weekDays = useMemo(() => {
    const from = weekStartOf(anchor);
    return Array.from({ length: 7 }, (_, i) => addDays(from, i));
  }, [anchor]);

  const monthCells = useMemo(() => {
    if (view !== 'month') return [];
    const first = `${anchor.slice(0, 7)}-01`;
    const lead = utcDay(first).getUTCDay();
    const last = Number(window_.end.slice(8, 10));
    const cells = [];
    for (let i = 0; i < lead; i += 1) cells.push(null);
    for (let i = 1; i <= last; i += 1) cells.push(`${anchor.slice(0, 7)}-${String(i).padStart(2, '0')}`);
    while (cells.length % 7) cells.push(null);
    return cells;
  }, [view, anchor, window_.end]);

  const selDay = byDay[selected] || { day: selected, scheduledMinutes: 0, workedMinutes: 0 };
  const selSched = scheduled.filter((x) => x.day === selected);
  const selWorked = worked.filter((w) => w.day === selected);
  const selCrew = coverage[selected] || [];
  const nowMins = minsIntoDay(new Date());

  // The tallest bar in the week decides the scale, with a floor of ten hours so
  // one short day does not render as a full-height column.
  const barMax = Math.max(600, ...days.map((x) => Math.max(x.scheduledMinutes, x.workedMinutes)));

  if (!idStaff) {
    return (
      <div className={lightMode ? `${s.page} ${s.lightPage}` : s.page}>
        <Header lightMode={lightMode} toggleLightMode={toggleLightMode} />
        <div className={s.body} style={{ gridTemplateColumns: '1fr' }}>
          <div className={s.attnEmpty}>
            This login is not linked to anybody on the roster, so it has no
            timecard. An admin can link it in Access &rarr; Users.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={lightMode ? `${s.page} ${s.lightPage}` : s.page}>
      <Header lightMode={lightMode} toggleLightMode={toggleLightMode} />

      <div className={s.shiftPage}>
        <div className={s.shiftTitleRow}>
          <div>
            <div className={s.shiftEyebrow}>
              {isSelf ? 'My shifts' : 'Staff shifts'}
              {' · '}
              {isSelf
                ? [user?.firstName, user?.lastName].filter(Boolean).join(' ')
                : (viewingName || 'choose a technician')}
            </div>
            <h1 className={s.shiftToday}>{dLong(today)}</h1>
          </div>

          {/* Whose week this is. Admin only — a technician has exactly one. */}
          {isAdmin && (
            <SelectMenu
              value={idStaff || ''}
              menuAlign="right"
              placeholder="Choose a technician…"
              onChange={(v) => setViewing(v || null)}
              options={roster.map(m => ({
                value: String(m.idStaff),
                label: fullName(m),
                note: String(m.idStaff) === String(ownStaffId)
                  ? `You · ${worksAt(m)}`
                  : worksAt(m),
              }))}
            />
          )}
        </div>

        {error && <div className={s.attnError}>{error}</div>}

        <div className={s.shiftGrid}>
          {/* ── left rail ─────────────────────────────────────────────── */}
          <div className={s.shiftCol}>
            <div className={s.clockHero} style={{ order: 1 }}>
              <div className={s.clockHeroTop}>
                <span className={`${s.clockDot} ${clockState === 'on' ? s.clockDotOn : ''}`} />
                <span className={`${s.clockState} ${clockState === 'on' ? s.clockStateOn : ''}`}>
                  {clockState === 'on' ? 'On shift'
                    : clockState === 'done' ? 'Clocked out' : 'Not clocked in'}
                </span>
                <span className={s.clockWhere}>
                  {openShift?.locationName || todaySched?.locationName || ''}
                  {todaySched?.bay ? ` · ${todaySched.bay}` : ''}
                </span>
              </div>

              <div className={s.clockBig}>
                {clockState === 'on'
                  ? `${dur(openShift.minutes)} so far`
                  : clockState === 'done'
                    ? `${dur(byDay[today]?.workedMinutes)} worked`
                    : todaySched ? `${tm(todaySched.startAt)} – ${tm(todaySched.endAt)}` : 'No shift today'}
              </div>
              <div className={s.clockLine}>
                {clockState === 'on' ? `Clocked in at ${tm(openShift.clockInAt)}`
                  : clockState === 'done' ? `${tm(lastToday?.clockInAt)} – ${tm(lastToday?.clockOutAt)}`
                  : todaySched ? `${todaySched.locationName || 'Scheduled'} · ${dur(todaySched.minutes)}`
                  : 'Nothing scheduled for you today'}
              </div>

              {(clockState === 'on' && todaySched) && (
                <div className={s.clockNext}>
                  Shift ends {tm(todaySched.endAt)} · {todaySched.locationName}
                </div>
              )}

              {todaySched && (
                <div className={s.clockProg}>
                  <div className={s.clockProgTrack}>
                    <div
                      className={`${s.clockProgFill} ${clockState === 'on' ? s.clockProgOn : ''}`}
                      style={{
                        width: `${Math.min(100, Math.max(0,
                          ((nowMins - minsIntoDay(todaySched.startAt))
                            / Math.max(1, minsIntoDay(todaySched.endAt) - minsIntoDay(todaySched.startAt))) * 100))}%`,
                      }}
                    />
                  </div>
                  <div className={s.clockProgRow}>
                    <span>{tm(todaySched.startAt)}</span>
                    <span className={s.clockProgMid}>
                      {clockState === 'on'
                        ? (nowMins < minsIntoDay(todaySched.endAt)
                            ? `${dur(minsIntoDay(todaySched.endAt) - nowMins)} left`
                            : 'Past shift end')
                        : nowMins < minsIntoDay(todaySched.startAt)
                          ? `Starts in ${dur(minsIntoDay(todaySched.startAt) - nowMins)}`
                          : nowMins > minsIntoDay(todaySched.endAt)
                            ? 'Shift over'
                            : `Started ${dur(nowMins - minsIntoDay(todaySched.startAt))} ago`}
                    </span>
                    <span>{tm(todaySched.endAt)}</span>
                  </div>
                </div>
              )}

              <button className={s.clockBtn} onClick={punch} disabled={busy || !canAct}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     strokeWidth="1.9" strokeLinecap="square" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" />
                </svg>
                <span>{busy ? 'Working…'
                  : clockState === 'on' ? 'Clock out'
                  : clockState === 'done' ? 'Clock in again' : 'Clock in'}</span>
              </button>

              <div className={s.clockFix}>
                <span>Wrong time?</span>
                <button className={s.clockFixLink} disabled={!canAct}
                        onClick={() => setSheet(selWorked.length
                          ? { newFixFor: selWorked[0] }
                          : todayWorked.length ? { newFixFor: todayWorked[0] } : { noShiftToFix: true })}>
                  Fix a clock time
                </button>
              </div>
            </div>

            <div className={s.shiftTiles} style={{ order: 5 }}>
              {TILES.map((t) => (
                <button key={t.kind} className={s.shiftTile} disabled={!canAct}
                        onClick={() => setSheet(t.kind === 'clock_fix'
                          ? (selWorked.length ? { newFixFor: selWorked[0] }
                             : todayWorked.length ? { newFixFor: todayWorked[0] } : { noShiftToFix: true })
                          : { newRequestKind: t.kind, label: t.label, day: selected })}>
                  <span className={s.shiftTileIcon}>
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                         strokeWidth="1.75" strokeLinecap="square" strokeLinejoin="round">
                      <path d={t.icon} />
                    </svg>
                  </span>
                  <span className={s.shiftTileLabel}>{t.label}</span>
                </button>
              ))}
            </div>

            <div className={s.weekCard} style={{ order: 6 }}>
              <div className={s.weekCardTop}>
                <span className={s.shiftLabel}>{view === 'week' ? 'This week' : 'This month'}</span>
                <span className={s.weekSched}>of {dur(totals.scheduledMinutes)} scheduled</span>
              </div>
              <div className={s.weekWorked}>{dur(totals.workedMinutes)} worked</div>

              <div className={s.weekBars}>
                {weekDays.map((key) => {
                  const day = byDay[key] || {};
                  return (
                    <div key={key} className={s.weekBarCol}>
                      <div className={s.weekBarTrack}>
                        <div className={s.weekBarSched}
                             style={{ height: `${((day.scheduledMinutes || 0) / barMax) * 100}%` }} />
                        <div className={s.weekBarWork}
                             style={{ height: `${((day.workedMinutes || 0) / barMax) * 100}%` }} />
                      </div>
                      <span className={`${s.weekBarDow} ${key === today ? s.weekBarDowToday : ''}`}>
                        {dowOf(key).charAt(0)}
                      </span>
                    </div>
                  );
                })}
              </div>

              <div className={s.weekLegend}>
                <span><i className={s.legendWork} />Worked</span>
                <span><i className={s.legendSched} />Scheduled</span>
              </div>
            </div>
          </div>

          {/* ── calendar ──────────────────────────────────────────────── */}
          <div className={s.shiftCol}>
            <div className={s.calCard} style={{ order: 2 }}>
              <div className={s.calHead}>
                <div className={s.calNav}>
                  <button className={s.calNavBtn} onClick={() => step(-1)} aria-label="Previous">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                         strokeWidth="1.75" strokeLinecap="square"><path d="M15 6l-6 6 6 6" /></svg>
                  </button>
                  <button className={s.calNavBtn} onClick={() => step(1)} aria-label="Next">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                         strokeWidth="1.75" strokeLinecap="square"><path d="M9 6l6 6-6 6" /></svg>
                  </button>
                </div>
                <div className={s.calTitleWrap}>
                  <div className={s.calTitle}>
                    {view === 'week'
                      ? `${dShort(window_.start)} – ${dShort(window_.end)}, ${window_.end.slice(0, 4)}`
                      : utcDay(window_.start).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' })}
                  </div>
                  <div className={s.calSub}>
                    {totals.scheduledShifts || 0} shift{totals.scheduledShifts === 1 ? '' : 's'} &middot; {dur(totals.scheduledMinutes)}
                  </div>
                </div>
                {(window_.start > today || window_.end < today) && (
                  <button className={s.calToday} onClick={() => { setAnchor(today); setSelected(today); }}>
                    Today
                  </button>
                )}
                <div className={s.calSeg}>
                  {['week', 'month'].map((v) => (
                    <button key={v} onClick={() => setView(v)}
                            className={`${s.calSegBtn} ${view === v ? s.calSegOn : ''}`}>
                      {v}
                    </button>
                  ))}
                </div>
              </div>

              {view === 'week' && !weekPosted && (
                <div className={s.calNotPosted}>
                  Your manager has not posted this week yet. You can still ask for days off.
                </div>
              )}

              {view === 'week' && (
                <>
                  {/* The week as seven cells. Below 1100 this replaces the time
                      grid entirely — an hour-by-hour column is unreadable at
                      phone width, and the day panel underneath carries the
                      detail the grid would have shown. */}
                  <div className={s.shiftStrip}>
                    {weekDays.map((key) => {
                      const day = byDay[key] || {};
                      const mins = key > today ? day.scheduledMinutes : day.workedMinutes;
                      return (
                        <button key={key} type="button"
                                onClick={() => setSelected(key)}
                                className={[s.stripDay,
                                  key === selected && s.stripDayOn,
                                  key === today && key !== selected && s.stripDayToday,
                                ].filter(Boolean).join(' ')}>
                          <span className={s.stripDow}>{dowOf(key)}</span>
                          <span className={s.stripNum}>{Number(key.slice(8, 10))}</span>
                          <span className={s.stripBar}
                                style={{ visibility: day.scheduledMinutes ? 'visible' : 'hidden' }} />
                          <span className={s.stripHrs}>{hrsShort(mins)}</span>
                        </button>
                      );
                    })}
                  </div>

                  <div className={s.calGrid}>
                    <div className={s.calGutter}>
                      {Array.from({ length: GRID_SPAN / 60 + 1 }, (_, i) => {
                        const h = GRID_FROM / 60 + i;
                        return (
                          <div key={h} className={s.calHour}>
                            {new Date(Date.UTC(2020, 0, 1, h)).toLocaleTimeString('en-US',
                              { hour: 'numeric', timeZone: 'UTC' })}
                          </div>
                        );
                      })}
                    </div>

                    {weekDays.map((key) => {
                      const day = byDay[key] || {};
                      return (
                        <button key={key} type="button"
                                onClick={() => setSelected(key)}
                                className={`${s.calCol} ${key === selected ? s.calColOn : ''} ${day.off ? s.calColOff : ''}`}>
                          <span className={s.calColHead}>
                            <span className={s.calColDow}>{dowOf(key)}</span>
                            <span className={s.calColNum}>{Number(key.slice(8, 10))}</span>
                            <span className={s.calColHrs}>{hrsShort(day.scheduledMinutes)}</span>
                          </span>

                          <span className={s.calColBody}>
                            {scheduled.filter((x) => x.day === key).map((x) => (
                              <span key={x.idSchedule} className={s.calBlock}
                                    style={{ top: pct(minsIntoDay(x.startAt)),
                                             height: span(minsIntoDay(x.startAt), minsIntoDay(x.endAt)) }}>
                                <span className={s.calBlockTime}>{tm(x.startAt)} – {tm(x.endAt)}</span>
                                <span className={s.calBlockWhere}>{x.locationName}{x.bay ? ` · ${x.bay}` : ''}</span>
                              </span>
                            ))}

                            {worked.filter((w) => w.day === key).map((w) => (
                              <span key={w.idShift} className={s.calPunch}
                                    style={{ top: pct(minsIntoDay(w.clockInAt)),
                                             height: span(minsIntoDay(w.clockInAt),
                                               w.clockOutAt ? minsIntoDay(w.clockOutAt) : nowMins) }} />
                            ))}

                            {day.pending && <span className={s.calPending} />}
                            {key === today && <span className={s.calNow} style={{ top: pct(nowMins) }} />}
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  <div className={s.calLegend}>
                    <span><i className={s.legendShift} />Shift</span>
                    <span><i className={s.legendPunch} />Your clock times</span>
                    <span><i className={s.legendOff} />Time off</span>
                    <span><i className={s.legendPending} />Request waiting</span>
                    <span><i className={s.legendNow} />Now</span>
                  </div>
                </>
              )}

              {view === 'month' && (
                <div className={s.monthGrid}>
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((h) => (
                    <div key={h} className={s.monthDow}>{h}</div>
                  ))}
                  {monthCells.map((key, i) => {
                    if (!key) return <div key={`pad${i}`} className={s.monthPad} />;
                    const day = byDay[key] || {};
                    return (
                      <button key={key} type="button"
                              onClick={() => { setSelected(key); setAnchor(key); setView('week'); }}
                              className={`${s.monthCell} ${key === selected ? s.monthCellOn : ''} ${day.off ? s.calColOff : ''}`}>
                        <span className={`${s.monthNum} ${key === today ? s.monthNumToday : ''}`}>
                          {Number(key.slice(8, 10))}
                        </span>
                        {day.scheduledMinutes > 0 && (
                          <span className={s.monthChip}>{hrsShort(day.scheduledMinutes)}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* ── right rail ────────────────────────────────────────────── */}
          <div className={s.shiftCol}>
            <div className={s.dayCard} style={{ order: 3 }}>
              <div className={s.dayHead}>
                <div className={s.dayEyebrowRow}>
                  <span className={s.shiftLabel}>{dLong(selected)}</span>
                  {selDay.off && <span className={`${s.statusPill} ${s.attnToneGray}`}>{selDay.off}</span>}
                  {selDay.pending && <span className={`${s.statusPill} ${s.attnToneWarn}`}>waiting</span>}
                </div>
                <div className={s.dayTitle}>
                  {selSched.length
                    ? `${tm(selSched[0].startAt)} – ${tm(selSched[selSched.length - 1].endAt)}`
                    : 'No shift'}
                </div>
                <div className={s.daySub}>
                  {selSched.length
                    ? `${selSched[0].locationName || '—'}${selSched[0].bay ? ` · ${selSched[0].bay}` : ''} · ${dur(selDay.scheduledMinutes)}`
                    : posted[weekStartOf(selected)] ? 'Nothing scheduled' : 'This week is not posted yet'}
                </div>
              </div>

              {selCrew.length > 0 && (
                <div className={s.whoOn}>
                  <div className={s.shiftLabel}>Who is on</div>
                  <div className={s.whoRows}>
                    {selCrew.map((c) => (
                      <div key={`${c.idStaff}-${c.startAt}`} className={s.whoRow}>
                        <span className={`${s.whoName} ${c.you ? s.whoNameYou : ''}`}>
                          {c.you ? 'You' : c.name}
                        </span>
                        <span className={s.whoTrack}>
                          <span className={s.whoBar}
                                style={{ left: pct(minsIntoDay(c.startAt)),
                                         width: span(minsIntoDay(c.startAt), minsIntoDay(c.endAt)) }} />
                          {selected === today && <span className={s.whoNow} style={{ left: pct(nowMins) }} />}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className={s.whoTicks}>
                    {['6 AM', '10 AM', '2 PM', '6 PM', '10 PM'].map((t) => <span key={t}>{t}</span>)}
                  </div>
                </div>
              )}

              {selWorked.length > 0 && (
                <div className={s.dayPunches}>
                  {selWorked.map((w) => (
                    <div key={w.idShift} className={s.dayPunchRow}>
                      <span>{w.open ? 'Clocked in' : 'Worked'}</span>
                      <span className={s.dayPunchVal}>
                        {w.open ? tm(w.clockInAt)
                          : `${tm(w.clockInAt)} – ${tm(w.clockOutAt)} · ${dur(w.minutes)}`}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              <div className={s.dayActionWrap}>
                <button className={s.dayAction} disabled={!canAct}
                        onClick={() => setSheet(selected < today && selWorked.length
                          ? { newFixFor: selWorked[0] }
                          : { newRequestKind: 'day_unavailable', label: 'Ask for a change', day: selected })}>
                  {selected < today ? 'Something wrong with this day?' : 'Ask for a change'}
                </button>
              </div>
            </div>

            <div className={s.railCard} style={{ order: 4 }}>
              <div className={s.railTitle}>Coming up</div>
              {upcoming.map((u) => {
                const uDay = localDay(u.startAt);
                return (
                <div key={u.idSchedule} className={s.upRow}>
                  <span className={s.upBar} />
                  <span className={s.upBody}>
                    <span className={s.upWhen}>
                      {uDay === today ? 'Today'
                        : uDay === addDays(today, 1) ? 'Tomorrow'
                        : `${dowOf(uDay)} ${dShort(uDay)}`}
                    </span>
                    <span className={s.upTime}>{tm(u.startAt)} &ndash; {tm(u.endAt)}</span>
                    <span className={s.upWhere}>{u.locationName}{u.bay ? ` · ${u.bay}` : ''}</span>
                  </span>
                  <span className={s.upHrs}>{hrsShort(u.minutes)}</span>
                </div>
                );
              })}
              {!upcoming.length && (
                <div className={s.railEmpty}>No posted shifts coming up yet.</div>
              )}
            </div>

            <div className={s.railCard} style={{ order: 7 }}>
              <div className={s.railTitleRow}>
                <span className={s.railTitle}>My requests</span>
                <span className={s.railCount}>
                  {requests.filter((r) => r.status === 'pending').length} waiting
                </span>
              </div>
              {requests.map((r) => (
                <button key={r.idRequest} className={s.reqRow} onClick={() => setSheet({ request: r })}>
                  <span className={s.reqBody}>
                    <span className={s.reqKind}>{REQ_LABEL[r.kind] || r.kind}</span>
                    <span className={s.reqDates}>
                      {r.fromDate
                        ? (iso(r.fromDate) === iso(r.toDate || r.fromDate)
                            ? dShort(iso(r.fromDate))
                            : `${dShort(iso(r.fromDate))} – ${dShort(iso(r.toDate))}`)
                        : [r.proposedClockInAt && `in ${tm(r.proposedClockInAt)}`,
                           r.proposedClockOutAt && `out ${tm(r.proposedClockOutAt)}`]
                            .filter(Boolean).join(' · ') || '—'}
                    </span>
                    {r.reason && <span className={s.reqDetail}>{r.reason}</span>}
                  </span>
                  {isAdmin && !isSelf && r.status === 'pending' ? (
                    <span className={s.reqDecide} onClick={(e) => e.stopPropagation()}>
                      <button className={s.reqDecline} disabled={busy}
                              onClick={() => decide(r, false)}>Decline</button>
                      <button className={s.reqApprove} disabled={busy}
                              onClick={() => decide(r, true)}>Approve</button>
                    </span>
                  ) : (
                    <span className={`${s.statusPill} ${s[STATUS_TONE[r.status]] || ''}`}>{r.status}</span>
                  )}
                </button>
              ))}
              {!requests.length && (
                <div className={s.railEmpty}>No requests yet. Tap a day to ask for a change.</div>
              )}
            </div>
          </div>
        </div>

        {isLoading && <div className={s.ledgerIntro}>Loading&hellip;</div>}
      </div>

      {/* The drawer takes its light theme from a `.lightRoot` ancestor in
          dashboard.module.css; this page's wrapper is staff.module.css. */}
      <div className={lightMode ? d.lightRoot : undefined} style={{ display: 'contents' }}>
        {sheet && (
          <RequestDecisionModal
            state={sheet}
            idStaff={idStaff}
            isAdmin={isAdmin && !isSelf}
            canAct={canAct}
            busy={busy}
            onDecide={(approve, note) => decide(sheet.request, approve, note)}
            defaultDay={sheet.day || selected}
            onSubmitted={() => { setSheet(null); refresh(); }}
            onClose={() => setSheet(null)}
          />
        )}
      </div>
    </div>
  );
}
